// Centralized theme configuration for the portfolio
// All colors, fonts, animations, and design tokens are defined here

export const themeConfig = {
  // Light theme configuration
  light: {
    colors: {
      // Backgrounds
      bg: {
        primary: '#fafafa',
        secondary: '#ffffff',
        tertiary: '#f6f8fa',
        card: '#ffffff',
        code: '#f5f5f5',
      },
      // Text colors
      text: {
        primary: '#24292f',
        secondary: '#57606a',
        accent: '#0969da',
        muted: '#656d76',
        code: '#1f2328',
      },
      // Brand colors
      brand: {
        primary: '#0969da',
        secondary: '#1f883d',
        accent: '#d73a49',
        highlight: '#ff6b35',
      },
      // UI elements
      ui: {
        border: '#d0d7de',
        borderHover: '#0969da',
        shadow: 'rgba(9, 105, 218, 0.1)',
        shadowHover: 'rgba(9, 105, 218, 0.2)',
        input: '#f6f8fa',
        inputBorder: '#d0d7de',
        buttonBg: '#0969da',
        buttonText: '#ffffff',
        buttonHover: '#0550ae',
      },
      // Status colors
      status: {
        success: '#1f883d',
        warning: '#bf8700',
        error: '#d73a49',
        info: '#0969da',
      },
    },
    fonts: {
      mono: "'Fira Code', 'JetBrains Mono', 'Source Code Pro', 'Courier New', monospace",
      sans: "'Ubuntu', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      display: "'Permanent Marker', cursive",
      handwriting: "'Caveat', cursive",
    },
  },

  // Dark theme configuration (terminal/coder aesthetic)
  dark: {
    colors: {
      // Backgrounds
      bg: {
        primary: '#0d1117',
        secondary: '#161b22',
        tertiary: '#0a0e27',
        card: '#161b22',
        code: '#0a0e27',
      },
      // Text colors
      text: {
        primary: '#e6edf3',
        secondary: '#c9d1d9',
        accent: '#00ff41',
        muted: '#8b949e',
        code: '#c9d1d9',
      },
      // Brand colors (terminal green aesthetic)
      brand: {
        primary: '#00ff41',
        secondary: '#61dafb',
        accent: '#ffd700',
        highlight: '#ff6b35',
      },
      // UI elements
      ui: {
        border: '#30363d',
        borderHover: '#00ff41',
        shadow: 'rgba(0, 255, 65, 0.1)',
        shadowHover: 'rgba(0, 255, 65, 0.3)',
        input: '#0d1117',
        inputBorder: '#30363d',
        buttonBg: '#00ff41',
        buttonText: '#0d1117',
        buttonHover: '#39ff14',
      },
      // Status colors
      status: {
        success: '#3fb950',
        warning: '#d29922',
        error: '#f85149',
        info: '#58a6ff',
      },
    },
    fonts: {
      mono: "'Fira Code', 'JetBrains Mono', 'Source Code Pro', 'Courier New', monospace",
      sans: "'Ubuntu', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      display: "'Permanent Marker', cursive",
      handwriting: "'Caveat', cursive",
    },
  },

  // Animation settings
  animations: {
    // Reduced, subtle animations for professional feel
    duration: {
      fast: '150ms',
      normal: '300ms',
      slow: '500ms',
    },
    easing: {
      ease: 'ease',
      easeIn: 'ease-in',
      easeOut: 'ease-out',
      easeInOut: 'ease-in-out',
      spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    },
    // Framer Motion variants
    fadeIn: {
      initial: { opacity: 0 },
      animate: { opacity: 1 },
      transition: { duration: 0.5 },
    },
    slideUp: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.5 },
    },
    slideDown: {
      initial: { opacity: 0, y: -20 },
      animate: { opacity: 1, y: 0 },
      transition: { duration: 0.5 },
    },
    slideLeft: {
      initial: { opacity: 0, x: 20 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.5 },
    },
    slideRight: {
      initial: { opacity: 0, x: -20 },
      animate: { opacity: 1, x: 0 },
      transition: { duration: 0.5 },
    },
    scale: {
      initial: { opacity: 0, scale: 0.95 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.3 },
    },
  },

  // Spacing scale (for consistent margins/paddings)
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },

  // Border radius
  radius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  // Breakpoints (for responsive design)
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
};

export default themeConfig;
